import { useRef, useState } from 'react';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { logEvent } from '@amplitude/analytics-browser';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { UploadFile } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import Typography from '../../componentsv2/Typography';
import InfoDrawer from '../../componentsv2/InfoDrawer';
import Form from '../../componentsv2/Form';
import { CheckboxField, DateField, SelectField, TextField } from '../../componentsv2/Field';
import Button from '../../componentsv2/Button';
import Tooltip from '../../componentsv2/Tooltip';
import Chip from '../../componentsv2/Chip';
import { AddIcon, TrashIcon } from '../../components/Icons';
import {
  deleteDocumentAsset as deleteCompanyStakeholderAsset,
  uploadStakeholderAssets as uploadCompanyStakeholderAssets,
} from '../../services/documents';
import { useSnackbar } from '../../context';
import { useTranslation } from '../../hooks/i18n';
import { docsCollectionEvents } from '../../constants/events';
import {
  getBusinessStructureOptions,
  getCountriesForShareholdersAndLegalReps,
  getDocumentSlotLabels,
  getGovernmentIdTypes,
  getRequiredDocumentSlots,
  getStakeholderAssetTypes,
  DocumentSlotKey,
  DOCUMENT_SLOT_KEYS,
  GOVERNMENT_ID_TYPE,
  IDENTITY_DOCUMENT_SIDE,
  MAX_AIPRISE_DOCUMENT_SIZE_BYTES,
  PERSON_TYPE,
  US_COUNTRY_CODE,
} from '../../constants/data';
import { baseURL } from '../../constants/api';
import { Nullable } from '../../types';
import { APIStakeholder } from '../../types/documents';
import { APIAsset, StakeholderAsset } from '../../types/application';
import { juridicalTaxIdTypes, physicalTaxIdTypes } from './utils';
import { COLORS } from '../../themev2';

const AddEditStakeholderDrawer = ({
  stakeholder,
  companyId,
  onAddStakeholder,
  onEditStakeholder,
  handleClose,
  showDrawer,
  isAdmin = false,
  isAccepted = false,
  isShareholder = false,
  stakeholderCopyKey,
  allowAddControllingPerson = false,
  parentName,
  uploadAssets = uploadCompanyStakeholderAssets,
  deleteAsset = deleteCompanyStakeholderAsset,
}: {
  stakeholder: Nullable<APIStakeholder>;
  companyId: number;
  onAddStakeholder: (companyId: number, values: any, files: StakeholderAsset[]) => Promise<any>;
  onEditStakeholder: (companyId: number, stakeholderId: number, values: any) => Promise<any>;
  onUpdateStakeholderAsset: (
    companyId: number,
    stakeholderId: number,
    assetId: number,
    values: any
  ) => Promise<any>;
  handleClose: () => void;
  showDrawer: boolean;
  isAdmin?: boolean;
  isAccepted?: boolean;
  isShareholder?: boolean;
  stakeholderCopyKey?: 'shareholders' | 'legalRepresentatives' | 'controllingPersons';
  allowAddControllingPerson?: boolean;
  parentName?: string;
  // Default to the company stakeholder endpoints; the LLC formation passes its own
  uploadAssets?: typeof uploadCompanyStakeholderAssets;
  deleteAsset?: typeof deleteCompanyStakeholderAsset;
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const isEdit = !!stakeholder;
  const copyKey = stakeholderCopyKey || (isShareholder ? 'shareholders' : 'legalRepresentatives');
  // when creating, array starts empty... when editing it starts with the shareholder assets
  const [files, setFiles] = useState<APIAsset[]>(isEdit ? stakeholder!.assets : []);
  const [isAddingDocument, setIsAddingDocument] = useState(false);
  // A non-passport document needs a front AND a back image; passport needs one.
  const [currentFrontFile, setCurrentFrontFile] = useState<Nullable<File>>(null);
  const [currentBackFile, setCurrentBackFile] = useState<Nullable<File>>(null);
  const [currentFileAssetType, setCurrentFileAssetType] = useState<string>('');
  // Which side ('front'/'back') the file picker is currently filling.
  const [activeSlotSide, setActiveSlotSide] = useState<Nullable<string>>(null);
  // Which company document slot the file picker is currently filling.
  const [activeCompanySlot, setActiveCompanySlot] = useState<Nullable<DocumentSlotKey>>(null);
  const [filesToUpload, setFilesToUpload] = useState<StakeholderAsset[]>([]);
  const [missingSlots, setMissingSlots] = useState<DocumentSlotKey[]>([]);
  const uploaderRef = useRef<any>(null);

  // Company documents of a juridical shareholder live on the same asset list as
  // the identity ones, tagged with the document slot as asset_type. Everything
  // that is not a slot is an identity document.
  const isIdentityAsset = (assetType: string) =>
    !(DOCUMENT_SLOT_KEYS as readonly string[]).includes(assetType);

  const getSlotFile = (slotKey: DocumentSlotKey) =>
    filesToUpload.find(({ assetType }) => assetType === slotKey)?.file;

  const getSlotAsset = (slotKey: DocumentSlotKey) => files.find((asset) => asset.assetType === slotKey);

  const handleSlotFileSelected = async (slotKey: DocumentSlotKey, selectedFile: File) => {
    setMissingSlots((prev) => prev.filter((slot) => slot !== slotKey));
    const asset = { file: selectedFile, assetType: slotKey } as StakeholderAsset;
    if (isEdit) {
      const data = await uploadAssets(companyId, stakeholder!.id, [asset], isAdmin, 'shareholders');
      setFiles(data.assets);
    } else {
      setFilesToUpload((prev) => [...prev.filter(({ assetType }) => assetType !== slotKey), asset]);
    }
  };

  const handleSlotDelete = (slotKey: DocumentSlotKey, assetId?: number) => {
    if (assetId) {
      deleteAsset(companyId, stakeholder!.id, assetId, isAdmin);
      setFiles((prev) => prev.filter((asset) => asset.id !== assetId));
      return;
    }
    setFilesToUpload((prev) => prev.filter(({ assetType }) => assetType !== slotKey));
  };

  const documentSideLabel = (assetType: string) => {
    if (assetType === IDENTITY_DOCUMENT_SIDE.FRONT)
      return t('client:documents.shareholders.identityDocument.front');
    if (assetType === IDENTITY_DOCUMENT_SIDE.BACK)
      return t('client:documents.shareholders.identityDocument.back');
    return assetType;
  };

  // Passport is single-sided; every other document type needs front + back.
  const currentDocNeedsBack = !!currentFileAssetType && currentFileAssetType !== GOVERNMENT_ID_TYPE.PASSPORT;

  const resetAddDocumentForm = (setFieldValue: (field: string, value: any) => void) => {
    setCurrentFrontFile(null);
    setCurrentBackFile(null);
    setCurrentFileAssetType('');
    setActiveSlotSide(null);
    setFieldValue('currentFileAssetType', '');
    setIsAddingDocument(false);
  };

  const handleIdentityDocumentComplete = async (
    frontFile: File,
    backFile: Nullable<File>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const assets: StakeholderAsset[] = [
      { assetType: IDENTITY_DOCUMENT_SIDE.FRONT, file: frontFile } as StakeholderAsset,
    ];
    if (backFile) {
      assets.push({ assetType: IDENTITY_DOCUMENT_SIDE.BACK, file: backFile } as StakeholderAsset);
    }
    if (isEdit) {
      const data = await uploadAssets(
        companyId,
        stakeholder!.id,
        assets,
        isAdmin,
        isShareholder ? 'shareholders' : 'legal_representatives'
      );
      setFiles(data.assets);
    } else {
      setFilesToUpload((prev) => [...prev, ...assets]);
    }
    resetAddDocumentForm(setFieldValue);
  };

  // Admins can save stakeholders with partial information; only the client-facing
  // flow enforces these fields as required.
  const requiredUnlessAdmin = <T extends Yup.AnySchema>(schema: T): T =>
    isAdmin ? schema : (schema.required(t('requiredField')) as T);

  const requiredWhenPhysical = () =>
    Yup.string().when('personType', {
      is: PERSON_TYPE.PHYSICAL,
      then: requiredUnlessAdmin(Yup.string()),
      otherwise: Yup.string(),
    });

  const requiredWhenJuridical = () =>
    Yup.string().when('personType', {
      is: PERSON_TYPE.JURIDICAL,
      then: requiredUnlessAdmin(Yup.string()),
      otherwise: Yup.string(),
    });

  const validationSchema = Yup.object().shape({
    fullName: Yup.string().required(t('requiredField')),
    personType: requiredUnlessAdmin(Yup.string()),
    nationality: requiredWhenPhysical(),
    percentageShare: Yup.number().when('isControllingPerson', {
      is: (isControllingPerson: boolean) => isShareholder || isControllingPerson,
      then: requiredUnlessAdmin(
        Yup.number()
          .min(1, t('minValue', { value: 1 }))
          .max(100, t('maxValue', { value: 100 }))
          .nullable()
      ),
      otherwise: Yup.number().nullable(),
    }),
    email: Yup.string()
      .email(t('emailInvalidError'))
      .when('personType', {
        is: PERSON_TYPE.PHYSICAL,
        then: requiredUnlessAdmin(Yup.string()),
        otherwise: Yup.string(),
      }),
    governmentIdIssuedIn: requiredWhenPhysical(),
    governmentIdType: requiredWhenPhysical(),
    governmentIdNumber: requiredWhenPhysical(),
    countryOfIncorporation: requiredWhenJuridical(),
    taxIdType: requiredWhenJuridical(),
    personIdNumber: requiredWhenJuridical(),
    dateOfIncorporation: Yup.mixed().when('personType', {
      is: PERSON_TYPE.JURIDICAL,
      then: requiredUnlessAdmin(Yup.mixed()).nullable(),
      otherwise: Yup.mixed().nullable(),
    }),
    businessStructure: Yup.string().when(['personType', 'countryOfIncorporation'], {
      is: (personType: string, countryOfIncorporation: string) =>
        personType === PERSON_TYPE.JURIDICAL && countryOfIncorporation === US_COUNTRY_CODE,
      then: requiredUnlessAdmin(Yup.string()),
      otherwise: Yup.string(),
    }),
  });

  return (
    <Formik
      initialValues={{
        personType: stakeholder?.personType || PERSON_TYPE.PHYSICAL,
        fullName: stakeholder?.fullName || '',
        nationality: stakeholder?.nationality || '',
        countryOfIncorporation: stakeholder?.countryOfIncorporation || '',
        stateOfIncorporation: stakeholder?.stateOfIncorporation || '',
        businessStructure: stakeholder?.businessStructure || '',
        dateOfIncorporation: stakeholder?.dateOfIncorporation ? dayjs(stakeholder.dateOfIncorporation) : null,
        personIdNumber: stakeholder?.personIdNumber || '',
        percentageShare: stakeholder?.percentageShare ?? null,
        isControllingPerson: stakeholder?.isControllingPerson || false,
        taxIdType: stakeholder?.taxIdType || '',
        dateOfBirth: stakeholder?.dateOfBirth ? dayjs(stakeholder.dateOfBirth) : null,
        address: stakeholder?.address || '',
        email: stakeholder?.email || '',
        phoneNumber: stakeholder?.phoneNumber || '',
        jobTitle: stakeholder?.jobTitle || '',
        governmentIdIssuedIn: stakeholder?.governmentIdIssuedIn || '',
        governmentIdType: stakeholder?.governmentIdType || '',
        governmentIdNumber: stakeholder?.governmentIdNumber || '',
        addressCountry: stakeholder?.addressCountry || '',
        addressState: stakeholder?.addressState || '',
        addressCity: stakeholder?.addressCity || '',
        addressZip: stakeholder?.addressZip || '',
        addressLine1: stakeholder?.address || stakeholder?.addressLine1 || '',
        addressLine2: stakeholder?.addressLine2 || '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        // A physical person is identified by a government ID; a juridical one by
        // the company documents its country of incorporation requires.
        if (values.personType === PERSON_TYPE.JURIDICAL) {
          const slotsWithoutFile = getRequiredDocumentSlots(
            values.countryOfIncorporation,
            values.businessStructure
          ).filter((slotKey) => !getSlotFile(slotKey) && !getSlotAsset(slotKey));
          if (!isAdmin && slotsWithoutFile.length > 0) {
            setMissingSlots(slotsWithoutFile);
            showSnackbar(t('client:documents.shareholders.companyDocuments.required'), 'error');
            setSubmitting(false);
            return;
          }
        } else {
          if (currentFrontFile || currentBackFile) {
            showSnackbar(t('client:documents.shareholders.identityDocument.finishUpload'), 'error');
            setSubmitting(false);
            return;
          }
          if (!isAdmin && [...files, ...filesToUpload].length === 0) {
            showSnackbar(t('client:documents.shareholders.identityDocument.required'), 'error');
            setSubmitting(false);
            return;
          }
        }
        const mappedValues = {
          ...values,
          dateOfBirth: values.dateOfBirth ? dayjs(values.dateOfBirth).format('YYYY-MM-DD') : '',
          dateOfIncorporation: values.dateOfIncorporation
            ? dayjs(values.dateOfIncorporation).format('YYYY-MM-DD')
            : '',
          percentageShare:
            (values.percentageShare as unknown as string) === '' ? null : values.percentageShare,
        };
        const submission = !isEdit
          ? onAddStakeholder(companyId, mappedValues, filesToUpload)
          : onEditStakeholder(companyId, stakeholder!.id, {
              ...mappedValues,
              address_line_1: mappedValues.addressLine1,
              address_line_2: mappedValues.addressLine2,
            });
        logEvent(docsCollectionEvents.SAVE_STAKEHOLDER_FORM, {
          Type: isShareholder ? 'Shareholder' : 'Legal Representative',
          'Full Name': values.fullName,
          'Type Of Person': values.personType,
          Participation: values.percentageShare,
          'Identification Number': values.personIdNumber,
          Email: values.email,
          Files: filesToUpload.map((f) => f.file.name),
        });
        submission
          .catch(() => {
            showSnackbar(t('common:error'), 'error');
          })
          .finally(() => {
            setSubmitting(false);
          });
      }}
    >
      {({ isSubmitting, values, submitForm, setFieldValue }) => {
        const isJuridical = values.personType === PERSON_TYPE.JURIDICAL;
        const slotLabels = getDocumentSlotLabels(t, values.countryOfIncorporation);
        return (
          <InfoDrawer
            showDrawer={showDrawer}
            handleClose={handleClose}
            submit={submitForm}
            isSubmitting={isSubmitting}
            disableSubmit={isAccepted}
            buttonLabel={
              isEdit ? t(`client:documents.shareholders.editBtn`) : t(`client:documents.shareholders.addBtn`)
            }
          >
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '24px',
                    lineHeight: '36px',
                    color: COLORS.gray[1200],
                  }}
                >
                  {isAccepted
                    ? t(`client:documents.${copyKey}.viewFormTitle`, { companyName: parentName })
                    : isEdit
                    ? t(`client:documents.${copyKey}.editFormTitle`, { companyName: parentName })
                    : t(`client:documents.${copyKey}.addFormTitle`, { companyName: parentName })}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '24px',
                    color: COLORS.gray[1100],
                  }}
                >
                  {t(`client:documents.${copyKey}.formDescription`)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Form trackingEvent={docsCollectionEvents.EDIT_STAKEHOLDER_FORM}>
                  <Grid container>
                    <Grid item xs={12}>
                      <SelectField
                        disabled={isAccepted}
                        name="personType"
                        label={t('client:documents.shareholders.personType.label')}
                        placeholder={t('client:documents.shareholders.personType.placeholder')}
                        options={[
                          {
                            label: t('client:documents.shareholders.physical'),
                            value: PERSON_TYPE.PHYSICAL,
                          },
                          {
                            label: t('client:documents.shareholders.juridical'),
                            value: PERSON_TYPE.JURIDICAL,
                          },
                        ]}
                        onChange={(value) => {
                          setFilesToUpload([]);
                          setMissingSlots([]);
                          if (value === PERSON_TYPE.JURIDICAL) {
                            setFieldValue('governmentIdIssuedIn', '');
                            setFieldValue('governmentIdType', '');
                            setFieldValue('governmentIdNumber', '');
                            setFieldValue('jobTitle', '');
                            setFieldValue('nationality', '');
                            setFieldValue('dateOfBirth', null);
                            setFieldValue('isControllingPerson', false);
                          } else {
                            setFieldValue('countryOfIncorporation', '');
                            setFieldValue('stateOfIncorporation', '');
                            setFieldValue('businessStructure', '');
                            setFieldValue('dateOfIncorporation', null);
                          }
                        }}
                      />
                    </Grid>
                    {isJuridical && (
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '21px' }}>
                          {t('client:documents.shareholders.personType.juridicalNotice')}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ backgroundColor: COLORS.gray[400], borderRadius: '4px', padding: '1em' }}>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '21px',
                          }}
                        >
                          {t(`client:documents.shareholders.detailsTitle.${values.personType}`)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        disabled={isAccepted}
                        name="fullName"
                        label={t(`client:documents.shareholders.fullName.label.${values.personType}`)}
                        placeholder={t(`client:documents.shareholders.fullName.label.${values.personType}`)}
                      />
                    </Grid>
                    {isShareholder ? (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            disabled={isAccepted}
                            name="percentageShare"
                            label={t('client:documents.shareholders.percentageShare.label')}
                            placeholder={t('client:documents.shareholders.percentageShare.placeholder')}
                          />
                        </Grid>
                        {values.personType === PERSON_TYPE.PHYSICAL && (
                          <Grid item xs={12}>
                            <Tooltip
                              title={
                                !allowAddControllingPerson && !stakeholder?.isControllingPerson
                                  ? t('activation:step4.addControllingPersonDisabledTooltip')
                                  : ''
                              }
                            >
                              <span>
                                <CheckboxField
                                  disabled={
                                    isAccepted ||
                                    (!allowAddControllingPerson && !stakeholder?.isControllingPerson)
                                  }
                                  name="isControllingPerson"
                                  label={t('client:documents.shareholders.isControllingPerson.label')}
                                />
                              </span>
                            </Tooltip>
                          </Grid>
                        )}
                      </>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <CheckboxField
                            disabled={isAccepted}
                            name="isControllingPerson"
                            label={t('client:documents.controllingPersons.isAlsoShareholder.label')}
                          />
                        </Grid>
                        {values.isControllingPerson && (
                          <Grid item xs={12} md={6}>
                            <TextField
                              disabled={isAccepted}
                              name="percentageShare"
                              label={t('client:documents.shareholders.percentageShare.label')}
                              placeholder={t('client:documents.shareholders.percentageShare.placeholder')}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    {isJuridical ? (
                      <>
                        <Grid item xs={12}>
                          <Box
                            sx={{ backgroundColor: COLORS.gray[400], borderRadius: '4px', padding: '1em' }}
                          >
                            <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '21px' }}>
                              {t('client:documents.shareholders.companyDetailsTitle')}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <SelectField
                            disabled={isAccepted}
                            name="countryOfIncorporation"
                            label={t('client:documents.shareholders.countryOfIncorporation.label')}
                            placeholder={t(
                              'client:documents.shareholders.countryOfIncorporation.placeholder'
                            )}
                            options={getCountriesForShareholdersAndLegalReps(t)}
                            onChange={() => {
                              setFieldValue('businessStructure', '');
                              setFilesToUpload([]);
                              setMissingSlots([]);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            optional
                            disabled={isAccepted}
                            name="stateOfIncorporation"
                            label={t('client:documents.shareholders.stateOfIncorporation.label')}
                            placeholder={t('client:documents.shareholders.stateOfIncorporation.placeholder')}
                          />
                        </Grid>
                        {values.countryOfIncorporation === US_COUNTRY_CODE && (
                          <Grid item xs={12} md={6}>
                            <SelectField
                              disabled={isAccepted}
                              name="businessStructure"
                              label={t('client:documents.shareholders.businessStructure.label')}
                              placeholder={t('client:documents.shareholders.businessStructure.placeholder')}
                              options={getBusinessStructureOptions(t, values.countryOfIncorporation)}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                          <DateField
                            disableFuture
                            disabled={isAccepted}
                            name="dateOfIncorporation"
                            label={t('client:documents.shareholders.dateOfIncorporation.label')}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          sx={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0' }}
                        >
                          <TextField
                            optional
                            disabled={isAccepted}
                            name="email"
                            label={t('client:documents.shareholders.contactEmail.label')}
                            placeholder={t('client:documents.shareholders.contactEmail.placeholder')}
                          />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} md={6}>
                          <SelectField
                            disabled={isAccepted}
                            name="nationality"
                            label={t(`client:documents.shareholders.nationality.label.${values.personType}`)}
                            placeholder={t(
                              `client:documents.shareholders.nationality.label.${values.personType}`
                            )}
                            options={getCountriesForShareholdersAndLegalReps(t)}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          sx={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0' }}
                        >
                          <TextField
                            disabled={isAccepted}
                            name="email"
                            label={t('client:documents.shareholders.email.label')}
                            placeholder={t('client:documents.shareholders.email.placeholder')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DateField
                            disableFuture
                            optional
                            disabled={isAccepted}
                            name="dateOfBirth"
                            label={t(`client:documents.shareholders.dateOfBirth.label.${values.personType}`)}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          sx={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0' }}
                        >
                          <TextField
                            optional
                            disabled={isAccepted}
                            name="jobTitle"
                            label={t('client:documents.shareholders.jobTitle.label')}
                            placeholder={t('client:documents.shareholders.jobTitle.placeholder')}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          sx={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0' }}
                        >
                          <TextField
                            optional
                            disabled={isAccepted}
                            name="phoneNumber"
                            label={t('client:documents.shareholders.phoneNumber.label')}
                            placeholder={t('client:documents.shareholders.phoneNumber.placeholder')}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ backgroundColor: COLORS.gray[400], borderRadius: '4px', padding: '1em' }}>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '21px',
                          }}
                        >
                          {t('client:documents.shareholders.taxIdTitle')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectField
                        disabled={isAccepted}
                        name="taxIdType"
                        label={t(`client:documents.shareholders.taxIdType.label`)}
                        placeholder={t(`client:documents.shareholders.taxIdType.placeholder`)}
                        options={isJuridical ? juridicalTaxIdTypes : physicalTaxIdTypes}
                        optional={!isJuridical}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        disabled={isAccepted}
                        name="personIdNumber"
                        label={t(`client:documents.shareholders.personIdNumber.label.${values.personType}`)}
                        placeholder={t(
                          `client:documents.shareholders.personIdNumber.label.${values.personType}`
                        )}
                        optional={!isJuridical}
                      />
                    </Grid>
                    {values.personType === PERSON_TYPE.PHYSICAL && (
                      <>
                        <Grid item xs={12}>
                          <Box
                            sx={{ backgroundColor: COLORS.gray[400], borderRadius: '4px', padding: '1em' }}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '21px',
                              }}
                            >
                              {t('client:documents.shareholders.governmentIdTitle')}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <SelectField
                            disabled={isAccepted}
                            name="governmentIdIssuedIn"
                            label={t(`client:documents.shareholders.governmentIdIssuedIn.label`)}
                            placeholder={t(`client:documents.shareholders.governmentIdIssuedIn.placeholder`)}
                            options={getCountriesForShareholdersAndLegalReps(t)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <SelectField
                            disabled={isAccepted}
                            name="governmentIdType"
                            label={t(`client:documents.shareholders.governmentIdType.label`)}
                            placeholder={t(`client:documents.shareholders.governmentIdType.placeholder`)}
                            options={getGovernmentIdTypes(t)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            disabled={isAccepted}
                            name="governmentIdNumber"
                            label={t(`client:documents.shareholders.governmentIdNumber.label`)}
                            placeholder={t(`client:documents.shareholders.governmentIdNumber.placeholder`)}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          backgroundColor: COLORS.gray[400],
                          borderRadius: '4px',
                          padding: '1em',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '21px',
                          }}
                        >
                          {isJuridical
                            ? t('client:documents.shareholders.companyDocumentsTitle')
                            : t('client:documents.shareholders.documentsTitle')}
                        </Typography>
                        {values.personType === PERSON_TYPE.PHYSICAL && (
                          <Box sx={{ height: '20px', display: 'flex', alignItems: 'center' }}>
                            <Button
                              startIcon={<AddIcon />}
                              type="text"
                              color="secondary"
                              isFullHeight={false}
                              onClick={() => setIsAddingDocument(true)}
                              disabled={isAddingDocument}
                            >
                              {t('client:documents.shareholders.addDocumentBtn')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '21px' }}>
                        {t(`client:documents.shareholders.documentsDescription.${values.personType}`)}
                      </Typography>
                    </Grid>
                    {isJuridical &&
                      getRequiredDocumentSlots(values.countryOfIncorporation, values.businessStructure).map(
                        (slotKey) => {
                          const slotFile = getSlotFile(slotKey);
                          const slotAsset = getSlotAsset(slotKey);
                          return (
                            <Grid item xs={12} key={slotKey}>
                              <Grid container alignItems="center">
                                <Grid item xs={5}>
                                  <Typography
                                    sx={{
                                      fontSize: '14px',
                                      fontWeight: 400,
                                      lineHeight: '21px',
                                      color: missingSlots.includes(slotKey)
                                        ? COLORS.feedback.danger
                                        : COLORS.gray[1200],
                                    }}
                                  >
                                    {slotLabels[slotKey]}
                                  </Typography>
                                </Grid>
                                <Grid
                                  item
                                  xs={5}
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  {slotFile ? (
                                    <Chip
                                      label={slotFile.name}
                                      size="small"
                                      sx={{
                                        backgroundColor: COLORS.feedback.successBackground,
                                        color: COLORS.feedback.success,
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        height: 'auto',
                                      }}
                                    />
                                  ) : slotAsset ? (
                                    <a
                                      href={`${baseURL}/assets/${slotAsset.uuid}`}
                                      download={slotAsset.fileName}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <Chip
                                        label={slotAsset.fileName}
                                        size="small"
                                        sx={{
                                          backgroundColor: COLORS.feedback.successBackground,
                                          color: COLORS.feedback.success,
                                          borderRadius: '4px',
                                          padding: '4px 8px',
                                          height: 'auto',
                                        }}
                                      />
                                    </a>
                                  ) : (
                                    <Button
                                      disabled={isAccepted}
                                      type="link"
                                      color="secondary"
                                      startIcon={<UploadFile />}
                                      onClick={() => {
                                        setActiveCompanySlot(slotKey);
                                        uploaderRef.current && uploaderRef.current.click();
                                      }}
                                    >
                                      {t('client:documents.shareholders.uploadBtn')}
                                    </Button>
                                  )}
                                </Grid>
                                <Grid
                                  item
                                  xs={2}
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  {(slotFile || slotAsset) && (
                                    <IconButton
                                      disabled={isAccepted}
                                      onClick={() => handleSlotDelete(slotKey, slotAsset?.id)}
                                    >
                                      <TrashIcon size="sm" />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Grid>
                            </Grid>
                          );
                        }
                      )}
                    <input
                      disabled={isAccepted}
                      type="file"
                      style={{ display: 'none' }}
                      ref={uploaderRef}
                      onChange={async (e: any) => {
                        const uploadedFile = (Array.from(e.target.files) as File[])[0];
                        e.target.value = '';
                        if (!uploadedFile) return;
                        if (uploadedFile.size > MAX_AIPRISE_DOCUMENT_SIZE_BYTES) {
                          showSnackbar(
                            t('client:documents.shareholders.identityDocument.fileTooLarge'),
                            'error'
                          );
                          return;
                        }
                        logEvent(docsCollectionEvents.UPLOAD_STAKEHOLDER_DOCUMENT);
                        if (activeCompanySlot) {
                          await handleSlotFileSelected(activeCompanySlot, uploadedFile);
                          setActiveCompanySlot(null);
                          return;
                        }
                        if (!activeSlotSide) return;
                        const isBack = activeSlotSide === IDENTITY_DOCUMENT_SIDE.BACK;
                        const frontFile = isBack ? currentFrontFile : uploadedFile;
                        const backFile = isBack ? uploadedFile : currentBackFile;
                        setActiveSlotSide(null);
                        // The document is only persisted once every side it requires is picked.
                        if (!frontFile || (currentDocNeedsBack && !backFile)) {
                          setCurrentFrontFile(frontFile);
                          setCurrentBackFile(backFile);
                          return;
                        }
                        await handleIdentityDocumentComplete(
                          frontFile,
                          currentDocNeedsBack ? backFile : null,
                          setFieldValue
                        );
                      }}
                    />
                    {values.personType === PERSON_TYPE.PHYSICAL && isAddingDocument && (
                      <Grid item xs={12}>
                        <Grid container rowSpacing={1}>
                          <Grid item xs={12} md={4}>
                            <SelectField
                              name="currentFileAssetType"
                              placeholder={t(`client:documents.shareholders.documentType.placeholder`)}
                              options={getStakeholderAssetTypes(t)}
                              onChange={(value) => {
                                setCurrentFileAssetType(value);
                                setCurrentFrontFile(null);
                                setCurrentBackFile(null);
                              }}
                              value={currentFileAssetType || ''}
                            />
                          </Grid>
                          {currentFileAssetType && (
                            <Grid
                              item
                              xs={12}
                              md={6}
                              sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {currentDocNeedsBack && (
                                  <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
                                    {t('client:documents.shareholders.identityDocument.front')}
                                  </Typography>
                                )}
                                {currentFrontFile ? (
                                  <Chip
                                    label={currentFrontFile.name}
                                    size="small"
                                    sx={{
                                      backgroundColor: COLORS.feedback.successBackground,
                                      color: COLORS.feedback.success,
                                      borderRadius: '4px',
                                      padding: '4px 8px',
                                      height: 'auto',
                                    }}
                                  />
                                ) : (
                                  <Button
                                    disabled={isAccepted}
                                    type="link"
                                    color="secondary"
                                    startIcon={<UploadFile />}
                                    onClick={() => {
                                      setActiveSlotSide(IDENTITY_DOCUMENT_SIDE.FRONT);
                                      uploaderRef.current && uploaderRef.current.click();
                                    }}
                                  >
                                    {t('client:documents.shareholders.uploadBtn')}
                                  </Button>
                                )}
                              </Box>
                              {currentDocNeedsBack && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
                                    {t('client:documents.shareholders.identityDocument.back')}
                                  </Typography>
                                  {currentBackFile ? (
                                    <Chip
                                      label={currentBackFile.name}
                                      size="small"
                                      sx={{
                                        backgroundColor: COLORS.feedback.successBackground,
                                        color: COLORS.feedback.success,
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        height: 'auto',
                                      }}
                                    />
                                  ) : (
                                    <Button
                                      disabled={isAccepted}
                                      type="link"
                                      color="secondary"
                                      startIcon={<UploadFile />}
                                      onClick={() => {
                                        setActiveSlotSide(IDENTITY_DOCUMENT_SIDE.BACK);
                                        uploaderRef.current && uploaderRef.current.click();
                                      }}
                                    >
                                      {t('client:documents.shareholders.uploadBtn')}
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </Grid>
                          )}
                          <Grid
                            item
                            xs={12}
                            md={2}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <IconButton
                              disabled={isAccepted}
                              onClick={() => resetAddDocumentForm(setFieldValue)}
                            >
                              <TrashIcon size="sm" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                    {filesToUpload
                      .filter(({ assetType }) => isIdentityAsset(assetType))
                      .map(({ file, assetType }, index) => (
                        <Grid item xs={12} key={`${assetType}-${index}`}>
                          <Grid container alignItems="center">
                            <Grid item xs={5}>
                              <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '21px' }}>
                                {documentSideLabel(assetType)}
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={5}
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <Chip
                                label={file.name}
                                size="small"
                                sx={{
                                  backgroundColor: '#E1FAED',
                                  color: '#11705F',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  height: 'auto',
                                }}
                              />
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <IconButton
                                disabled={isAccepted}
                                onClick={() => {
                                  setFilesToUpload((prev) => prev.filter((_, _index) => _index !== index));
                                }}
                              >
                                <TrashIcon size="sm" />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}
                    {files
                      .filter((asset) => isIdentityAsset(asset.assetType))
                      .map((file: any, index) => {
                        const url = !!file.uuid
                          ? `${baseURL}/assets/${file.uuid}`
                          : URL.createObjectURL(new Blob([file]));
                        return (
                          <Grid item xs={12} key={file.id}>
                            <Grid container>
                              <Grid item xs={6} md={5}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '21px' }}>
                                  {documentSideLabel(file.assetType)}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                xs={6}
                                md={5}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <a href={url} download={file.fileName} target="_blank" rel="noreferrer">
                                  <Chip
                                    label={file.fileName}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#E1FAED',
                                      color: '#11705F',
                                      borderRadius: '4px',
                                      padding: '4px 8px',
                                      height: 'auto',
                                    }}
                                  />
                                </a>
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <IconButton
                                  disabled={isAccepted}
                                  onClick={() => {
                                    deleteAsset(companyId, stakeholder!.id, file.id, isAdmin);
                                    setFiles((prev) => prev.filter((_, _index) => _index !== index));
                                  }}
                                >
                                  <TrashIcon size="sm" />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Grid>
                        );
                      })}
                    <Grid item xs={12}>
                      <Box sx={{ backgroundColor: COLORS.gray[400], borderRadius: '4px', padding: '1em' }}>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '21px',
                          }}
                        >
                          {t('client:documents.shareholders.addressTitle')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectField
                        optional
                        disabled={isAccepted}
                        name="addressCountry"
                        label={t(`client:documents.shareholders.addressCountry.label`)}
                        placeholder={t(`client:documents.shareholders.addressCountry.label`)}
                        options={getCountriesForShareholdersAndLegalReps(t)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        optional
                        disabled={isAccepted}
                        name="addressState"
                        label={t('client:documents.shareholders.addressState.label')}
                        placeholder={t('client:documents.shareholders.addressState.placeholder')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        optional
                        disabled={isAccepted}
                        name="addressCity"
                        label={t('client:documents.shareholders.addressCity.label')}
                        placeholder={t('client:documents.shareholders.addressCity.placeholder')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        optional
                        disabled={isAccepted}
                        name="addressZip"
                        label={t('client:documents.shareholders.addressZip.label')}
                        placeholder={t('client:documents.shareholders.addressZip.placeholder')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        optional
                        disabled={isAccepted}
                        name="addressLine1"
                        label={t('client:documents.shareholders.addressLine1.label')}
                        placeholder={t('client:documents.shareholders.addressLine1.placeholder')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        optional
                        disabled={isAccepted}
                        name="addressLine2"
                        label={t('client:documents.shareholders.addressLine2.label')}
                        placeholder={t('client:documents.shareholders.addressLine2.placeholder')}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </Grid>
            </Grid>
          </InfoDrawer>
        );
      }}
    </Formik>
  );
};

export default AddEditStakeholderDrawer;
